import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Shield, Music, Info, DollarSign } from 'lucide-react';
import '../HistoryPage.css';

/**
 * The body of a completed scan report: score cards, guideline checks, policy
 * issues, cautions, suggestions and monetization issues. Shared by the
 * owner's report view (HistoryPage) and the public shared-report page, so the
 * two can never drift apart.
 *
 * @param {object} data       the job's `data` (overall_score, violations, ...)
 * @param {string} platform   'youtube' | 'tiktok' | ...
 * @param {object} [guidelines] guidelines keyed by platform, for the
 *                              "View <Platform> Guidelines" link (optional)
 */
function ScanReportBody({ data, platform = 'youtube', guidelines = null }) {
  const overallScore = data?.overall_score;
  const violations = data?.violations || [];
  const cautions = data?.cautions || [];
  const guidelineChecks = data?.guideline_checks || [];
  const adRevenueScore = data?.ad_revenue_score;
  const adRevenueRiskLevel = data?.ad_revenue_risk_level;
  const adRevenueIssues = data?.ad_revenue_issues || [];
  const fyfScore = data?.fyf_score;
  const fyfEligibilityStatus = data?.fyf_eligibility_status;
  const fyfIssues = data?.fyf_issues || [];
  const platformName = platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();

  return (
    <>
      {/* Score Summary Cards */}
      {(() => {
        const hasViolations = violations && violations.length > 0;
        const hasAudioIssues = violations?.some(v => v.source === 'audio');
        const isCompliant = overallScore > 70 && !hasViolations;
        const needsReview = overallScore > 70 && hasViolations;
        const scoreColorClass = isCompliant ? 'high' : (needsReview || overallScore > 40) ? 'medium' : 'low';

        let complianceStatus, complianceSubtitle, ComplianceIcon, complianceIconClass, complianceCardClass;
        if (isCompliant) {
          complianceStatus = 'Safe to publish';
          complianceSubtitle = 'No policy violations detected';
          ComplianceIcon = CheckCircle;
          complianceIconClass = 'success';
          complianceCardClass = 'compliant';
        } else if (needsReview) {
          complianceStatus = hasAudioIssues ? 'Copyright issues found' : 'Issues found — review before publishing';
          complianceSubtitle = 'Content may be removed or restricted';
          ComplianceIcon = AlertTriangle;
          complianceIconClass = 'warning';
          complianceCardClass = 'needs-review';
        } else {
          complianceStatus = 'At risk of removal';
          complianceSubtitle = 'Content likely violates platform policy';
          ComplianceIcon = XCircle;
          complianceIconClass = 'error';
          complianceCardClass = 'non-compliant';
        }

        const hasMonetization = (platform.toLowerCase() === 'youtube' && adRevenueRiskLevel) ||
                                (platform.toLowerCase() === 'tiktok' && fyfEligibilityStatus);
        const monetizationScore = platform.toLowerCase() === 'youtube' ? adRevenueScore : fyfScore;
        const monetizationClass = platform.toLowerCase() === 'youtube'
          ? adRevenueRiskLevel
          : fyfEligibilityStatus === 'eligible' ? 'low' : fyfEligibilityStatus === 'at_risk' ? 'medium' : 'high';
        const monetizationScoreClass = monetizationClass === 'low' ? 'high' : monetizationClass === 'medium' ? 'medium' : 'low';

        const monetizationTitle = 'Earnings Compliance';
        let monetizationStatus, monetizationSubtitle;
        if (platform.toLowerCase() === 'youtube') {
          if (adRevenueRiskLevel === 'low') { monetizationStatus = 'Fully advertiser-friendly'; monetizationSubtitle = 'Meets YouTube\'s Advertiser-Friendly Content Guidelines'; }
          else if (adRevenueRiskLevel === 'medium') { monetizationStatus = 'Some sections may limit ads'; monetizationSubtitle = 'Based on YouTube\'s Advertiser-Friendly Content Guidelines'; }
          else { monetizationStatus = 'Content not advertiser-friendly'; monetizationSubtitle = 'Based on YouTube\'s Advertiser-Friendly Content Guidelines'; }
        } else {
          if (fyfEligibilityStatus === 'eligible') { monetizationStatus = 'Eligible for FYF'; monetizationSubtitle = 'Meets TikTok\'s For You Feed eligibility criteria'; }
          else if (fyfEligibilityStatus === 'at_risk') { monetizationStatus = 'Reach may be reduced'; monetizationSubtitle = 'Some content may affect FYF distribution'; }
          else { monetizationStatus = 'Not eligible for FYF'; monetizationSubtitle = 'Does not meet TikTok\'s For You Feed eligibility criteria'; }
        }

        return (
          <div className={`history-score-cards${hasMonetization ? '' : ' single'}`}>
            {/* Policy Compliance Card */}
            <div className={`history-score-card ${complianceCardClass}`}>
              <div className="history-score-card-header">
                <Shield size={16} className={`history-score-card-icon ${complianceIconClass}`} />
                <span className="history-score-card-label">Policy Compliance</span>
                <span className="history-score-card-tooltip">Risk of removal or restriction</span>
              </div>
              <div className="history-score-card-number-row">
                <span className={`history-score-card-number ${scoreColorClass}`}>{overallScore}</span>
                <span className="history-score-card-denom">/100</span>
              </div>
              <div className="history-score-bar-wrapper history-score-card-bar">
                <div className={`history-score-bar ${scoreColorClass}`} style={{ width: `${overallScore}%` }} />
              </div>
              <div className={`history-score-card-status ${complianceIconClass}`}>
                <ComplianceIcon size={13} />
                <span>{complianceStatus}</span>
              </div>
              <p className="history-score-card-subtitle">{complianceSubtitle}</p>
            </div>

            {/* Monetization Card */}
            {hasMonetization && (
              <div className={`history-score-card monetization risk-${monetizationClass}`}>
                <div className="history-score-card-header">
                  <DollarSign size={16} className={`history-score-card-icon monetization-${monetizationClass}`} />
                  <span className="history-score-card-label">{monetizationTitle}</span>
                  <span className="history-score-card-tooltip">Separate from removal — affects earnings</span>
                </div>
                {monetizationScore != null && (<>
                  <div className="history-score-card-number-row">
                    <span className={`history-score-card-number ${monetizationScoreClass}`}>{monetizationScore}</span>
                    <span className="history-score-card-denom">/100</span>
                  </div>
                  <div className="history-score-bar-wrapper history-score-card-bar">
                    <div className={`history-score-bar ${monetizationScoreClass}`} style={{ width: `${monetizationScore}%` }} />
                  </div>
                </>)}
                <div className={`history-score-card-status monetization-${monetizationClass}`}>
                  {monetizationClass === 'low'
                    ? <CheckCircle size={13} />
                    : monetizationClass === 'medium'
                    ? <AlertTriangle size={13} />
                    : <XCircle size={13} />}
                  <span>{monetizationStatus}</span>
                </div>
                <p className="history-score-card-subtitle">{monetizationSubtitle}</p>
              </div>
            )}
          </div>
        );
      })()}

      {/* Guideline Checks Summary */}
      {guidelineChecks && guidelineChecks.length > 0 && (
        <div className="history-guideline-checks-section">
          <div className="history-guideline-checks-header">
            <Shield className="history-guideline-checks-icon" />
            <h3>Guideline Checks Summary</h3>
            {guidelines && guidelines[platform.toLowerCase()] && (
              <a
                href={guidelines[platform.toLowerCase()].guidelines_url}
                target="_blank"
                rel="noopener noreferrer"
                className="history-guidelines-link"
              >
                View {platformName} Guidelines
              </a>
            )}
          </div>
          <div className="history-guideline-checks-grid">
            {guidelineChecks.map((check, index) => (
              <div
                key={check.id || index}
                className={`history-guideline-check-item ${check.status === 'pass' ? 'pass' : 'fail'}`}
              >
                <div className={`history-guideline-check-status ${check.status}`}>
                  {check.status === 'pass' ? (
                    <CheckCircle className="history-check-icon pass" />
                  ) : (
                    <AlertTriangle className="history-check-icon fail" />
                  )}
                </div>
                <div className="history-guideline-check-content">
                  <div className="history-guideline-check-name">{check.name}</div>
                  <div className="history-guideline-check-group">{check.group}</div>
                  <div className="history-guideline-check-details">{check.details}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Policy Issues Section */}
      {violations && violations.length > 0 && (
        <div className="history-issues-section">
          <div className="history-issues-section-header">
            <h3>Policy Issues</h3>
            <p className="history-issues-section-subtitle">These sections risk your content being <strong>removed or restricted</strong> by {platformName}.</p>
          </div>
          <ul className="history-issues-list">
            {violations.map((violation, index) => {
              const getTimeStr = () => {
                if (violation.metadata?.timestamp_formatted) {
                  return violation.metadata.timestamp_formatted;
                }
                if (typeof violation.metadata?.time === 'number') {
                  const totalSeconds = violation.metadata.time;
                  const minutes = Math.floor(totalSeconds / 60);
                  const seconds = Math.floor(totalSeconds % 60);
                  return `${minutes}:${String(seconds).padStart(2, '0')}`;
                }
                const time = violation.metadata?.time;
                if (time?.start) {
                  const hours = time.start?.hour || 0;
                  const minutes = (time.start?.minute || 0) + (hours * 60);
                  const seconds = time.start?.second || 0;
                  return `${minutes}:${String(seconds).padStart(2, '0')}`;
                }
                return null;
              };
              const timeStr = getTimeStr();
              const isVisual = violation.source === 'visual';
              const isAudio = violation.source === 'audio';
              const isTextOverlay = violation.source === 'text_overlay';
              const frameUrl = violation.metadata?.frame_url;
              const musicInfo = violation.metadata?.music_info;

              return (
                <li key={index} className={`history-issue-item ${isVisual ? 'visual-issue' : ''} ${isAudio ? 'audio-issue' : ''} ${isTextOverlay ? 'text-overlay-issue' : ''}`}>
                  <div className={`history-issue-icon-wrapper ${isAudio ? 'audio' : ''}`}>
                    {isAudio ? (
                      <Music className="history-issue-icon audio" />
                    ) : (
                      <AlertTriangle className="history-issue-icon" />
                    )}
                  </div>
                  <div className="history-issue-content">
                    {isAudio && musicInfo && (
                      <div className="history-issue-music-info">
                        <div className="history-music-title">{musicInfo.title || 'Unknown Track'}</div>
                        <div className="history-music-artist">{musicInfo.artist || 'Unknown Artist'}</div>
                        {musicInfo.album && (
                          <div className="history-music-album">Album: {musicInfo.album}</div>
                        )}
                      </div>
                    )}
                    {(isVisual || isTextOverlay) && frameUrl && (
                      <div className="history-issue-frame-thumbnail">
                        <img src={frameUrl} alt={isTextOverlay ? 'Frame with on-screen text' : 'Flagged frame'} />
                      </div>
                    )}
                    <div className="history-issue-text-row">
                      {isTextOverlay && (
                        <span className="history-issue-source-tag">On-screen text</span>
                      )}
                      {timeStr && (
                        <span className="history-issue-time">[{timeStr}]</span>
                      )}
                      <span className="history-issue-text">{violation.text}</span>
                    </div>
                    {violation.guideline_link && (
                      <a
                        href={violation.guideline_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="history-issue-link"
                      >
                        View platform guidelines
                      </a>
                    )}
                    {violation.suggested_fix && (
                      <div className="history-issue-suggested-fix">
                        <span className="suggested-fix-label">Suggested Fix:</span> {violation.suggested_fix}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Cautions Section */}
      {cautions && cautions.length > 0 && (
        <div className="cautions-section">
          <h3>Cautions to Consider:</h3>
          <ul className="cautions-list">
            {cautions.map((caution, index) => (
              <li key={index} className="caution-item">
                <div className="caution-icon-wrapper">
                  <Info className="caution-icon" />
                </div>
                <div className="caution-content">
                  <div className="caution-concern">{caution.concern}</div>
                  {caution.text && (
                    <div className="caution-text">"{caution.text}"</div>
                  )}
                  {caution.suggestion && (
                    <div className="caution-suggestion">
                      <span className="suggestion-label">Suggestion:</span> {caution.suggestion}
                    </div>
                  )}
                  {caution.score_impact && (
                    <div className="caution-impact">Score impact: {caution.score_impact} points</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Suggestions Section */}
      {violations && violations.some(v => v.suggested_fix) && (
        <div className="history-suggestions-section">
          <h3>Creative Suggestions:</h3>
          <ul className="history-suggestions-list">
            {(() => {
              const seen = new Set();
              return violations
                .filter(v => {
                  if (!v.suggested_fix || seen.has(v.suggested_fix)) return false;
                  seen.add(v.suggested_fix);
                  return true;
                })
                .map((violation, index) => (
                  <li key={index} className="history-suggestion-item">
                    <span className="history-suggestion-icon">💡</span>
                    <span className="history-suggestion-text">
                      <strong>{violation.category}:</strong> {violation.suggested_fix}
                    </span>
                  </li>
                ));
            })()}
          </ul>
        </div>
      )}

      {/* Monetization Issues Section */}
      {(() => {
        const isYouTube = platform.toLowerCase() === 'youtube';
        const isTikTok = platform.toLowerCase() === 'tiktok';
        const hasAdIssues = isYouTube && adRevenueIssues.length > 0;
        const hasFyfIssues = isTikTok && fyfIssues.length > 0;
        if (!hasAdIssues && !hasFyfIssues) return null;

        const issues = hasAdIssues ? adRevenueIssues : fyfIssues;
        const sectionTitle = isYouTube ? 'Advertiser-Friendly Content Guidelines' : 'For You Feed Eligibility Issues';
        const sectionSubtitle = isYouTube
          ? 'These sections may limit or disable ads on your video. Your content can still stay up — this is separate from policy violations.'
          : 'These sections may prevent your video from appearing in the For You Feed, reducing organic reach.';
        const guidelinesUrl = isYouTube
          ? 'https://support.google.com/youtube/answer/6162278'
          : 'https://www.tiktok.com/safety/en/policies-and-engagement/fyf-standards';

        return (
          <div className="history-monetization-section">
            <div className="history-issues-section-header">
              <div className="history-issues-section-title-row">
                <h3>{sectionTitle}</h3>
                <a
                  href={guidelinesUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="history-guidelines-link"
                >
                  View official guidelines ↗
                </a>
              </div>
              <p className="history-issues-section-subtitle">{sectionSubtitle}</p>
            </div>
            <ul className="history-ad-revenue-issues">
              {issues.map((issue, index) => {
                const impactClass = issue.impact === 'no_ads' || issue.impact === 'fyf_ineligible' ? 'no_ads' : 'limited_ads';
                const impactLabel = issue.impact === 'no_ads' ? 'Ads Disabled'
                  : issue.impact === 'fyf_ineligible' ? 'FYF Ineligible'
                  : issue.impact === 'age_restricted' ? 'Age Restricted'
                  : 'Reduced Earnings';
                return (
                  <li key={index} className={`history-ad-revenue-issue impact-${impactClass}`}>
                    <div className="history-ad-revenue-issue-header">
                      <span className="history-ad-revenue-issue-category">{issue.category}</span>
                      <span className={`history-ad-revenue-impact-badge ${impactClass}`}>{impactLabel}</span>
                    </div>
                    {issue.text && (
                      <blockquote className="history-ad-revenue-issue-quote">"{issue.text}"</blockquote>
                    )}
                    {issue.explanation && (
                      <p className="history-ad-revenue-issue-explanation">{issue.explanation}</p>
                    )}
                    {issue.guideline_link && (
                      <a
                        href={issue.guideline_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="history-issue-link"
                      >
                        View guidelines ↗
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })()}
    </>
  );
}

export default ScanReportBody;
